import { CategoryNode, TYPE_HAS_CHECKBOX } from './growCategoryRestriction.types';

export type RestrictionFilter = 'all' | 'restricted' | 'active';

/** L4 (productType) is the leaf; every higher level can load children via the default API. */
export const isExpandableNode = (node: CategoryNode): boolean => !TYPE_HAS_CHECKBOX[node.typeName];

/**
 * Unify search-tree and children-API node shapes:
 * non-leaf levels always advertise expandability so lazy children fetch can run.
 */
export const normalizeCategoryTree = (nodes: CategoryNode[]): CategoryNode[] =>
    nodes.map((n) => {
        const hasPreloadedChildren = Array.isArray(n.children) && n.children.length > 0;
        const expandable = isExpandableNode(n);
        return {
            ...n,
            hasChildren: expandable,
            isExpanded: hasPreloadedChildren ? true : !!n.isExpanded,
            isLoadingChildren: false,
            children: hasPreloadedChildren ? normalizeCategoryTree(n.children!) : n.children,
        };
    });

/** Merge lazy children-API results with any preloaded search-tree branch under the same ids. */
export const mergeChildLists = (existing: CategoryNode[] = [], incoming: CategoryNode[]): CategoryNode[] => {
    const existingById = new Map(existing.map((n) => [n.categoryId, n]));

    return normalizeCategoryTree(incoming).map((apiNode) => {
        const prev = existingById.get(apiNode.categoryId);
        if (!prev) return apiNode;

        const hasPrevChildren = Array.isArray(prev.children) && prev.children.length > 0;
        const hasApiChildren = Array.isArray(apiNode.children) && apiNode.children.length > 0;

        return {
            ...apiNode,
            isExpanded: prev.isExpanded || hasPrevChildren,
            children: hasApiChildren
                ? mergeChildLists(prev.children ?? [], apiNode.children!)
                : hasPrevChildren
                  ? prev.children
                  : apiNode.children,
        };
    });
};

export const updateNode = (
    nodes: CategoryNode[],
    targetId: number,
    updater: (n: CategoryNode) => CategoryNode
): CategoryNode[] =>
    nodes.map((node) => {
        if (node.categoryId === targetId) return updater(node);
        if (node.children?.length) return { ...node, children: updateNode(node.children, targetId, updater) };
        return node;
    });

export const collectRestrictedIds = (nodes: CategoryNode[]): Set<number> => {
    const ids = new Set<number>();
    const walk = (list: CategoryNode[]) => {
        for (const n of list) {
            if (n.isRestricted && TYPE_HAS_CHECKBOX[n.typeName]) ids.add(n.categoryId);
            if (n.children?.length) walk(n.children);
        }
    };
    walk(nodes);
    return ids;
};

/** Collect all leaf (checkbox-capable) nodes from a tree. */
export const collectLeafNodes = (nodes: CategoryNode[]): CategoryNode[] => {
    const result: CategoryNode[] = [];
    const walk = (list: CategoryNode[]) => {
        for (const n of list) {
            if (TYPE_HAS_CHECKBOX[n.typeName]) result.push(n);
            if (n.children?.length) walk(n.children);
        }
    };
    walk(nodes);
    return result;
};

export const countAllNodes = (nodes: CategoryNode[]): number =>
    nodes.reduce((sum, n) => sum + 1 + countAllNodes(n.children ?? []), 0);

export const countLeafNodes = (nodes: CategoryNode[]): number =>
    nodes.reduce(
        (sum, n) =>
            sum + (TYPE_HAS_CHECKBOX[n.typeName] ? 1 : 0) + countLeafNodes(n.children ?? []),
        0
    );

/** Collect every node id that currently has children. */
export const collectExpandableIds = (nodes: CategoryNode[], acc: Set<number> = new Set()): Set<number> => {
    for (const n of nodes) {
        if (n.children && n.children.length > 0) {
            acc.add(n.categoryId);
            collectExpandableIds(n.children, acc);
        }
    }
    return acc;
};

export const collapseAllNodes = (nodes: CategoryNode[]): CategoryNode[] =>
    nodes.map((n) => ({
        ...n,
        isExpanded: false,
        isLoadingChildren: false,
        // Keep loaded children so re-expand can merge with a fresh children API call.
        children: n.children?.length ? collapseAllNodes(n.children) : n.children,
    }));

/** Keep leaf matches and ancestor branches that still have visible children. */
export const filterRestrictionTree = (
    nodes: CategoryNode[],
    filter: RestrictionFilter,
    restrictedIds: Set<number>
): CategoryNode[] => {
    if (filter === 'all') return nodes;

    return nodes.reduce<CategoryNode[]>((acc, node) => {
        const filteredChildren = filterRestrictionTree(node.children ?? [], filter, restrictedIds);
        const hasCheckbox = TYPE_HAS_CHECKBOX[node.typeName];
        const isRestricted = restrictedIds.has(node.categoryId);

        if (hasCheckbox) {
            const matches = filter === 'restricted' ? isRestricted : !isRestricted;
            if (matches) acc.push({ ...node, children: filteredChildren });
        } else if (filteredChildren.length > 0) {
            acc.push({ ...node, children: filteredChildren });
        }

        return acc;
    }, []);
};

/** Insert a node + its ancestor path into the restriction tree (additive only). */
const insertPath = (tree: CategoryNode[], path: CategoryNode[]): CategoryNode[] => {
    if (path.length === 0) return tree;
    const [head, ...rest] = path;
    const existingIndex = tree.findIndex((n) => n.categoryId === head.categoryId);
    if (existingIndex === -1) {
        return [...tree, { ...head, isExpanded: true, children: insertPath([], rest) }];
    }
    return tree.map((n, i) =>
        i !== existingIndex ? n : { ...n, isExpanded: true, children: insertPath(n.children ?? [], rest) }
    );
};

export const insertIntoRestrictionTree = (
    tree: CategoryNode[],
    node: CategoryNode,
    ancestors: CategoryNode[]
): CategoryNode[] => insertPath(tree, [...ancestors, node]);

/** Normalize varied children/search API payloads into a CategoryNode[]. */
export const extractCategoryNodes = (res: any): CategoryNode[] => {
    const data = res?.data ?? res ?? {};
    if (Array.isArray(data)) return data;
    const list = data?.children ?? data?.items ?? data?.categories ?? res?.children ?? [];
    return Array.isArray(list) ? list : [];
};

export const getSelectedCountryId = (): number | '' => {
    try {
        const raw = localStorage.getItem('selectedCountry');
        if (!raw) return '';
        return JSON.parse(raw)?.countryId ?? '';
    } catch {
        return '';
    }
};

export const getApiErrorMessage = (err: any, fallback: string): string =>
    err?.response?.data?.message ?? err?.message ?? fallback;

export const hasPendingRestrictionChanges = (
    restrictionTree: CategoryNode[],
    restrictedIds: Set<number>,
    baselineRestrictedIds: Set<number>
): boolean => {
    const leaves = collectLeafNodes(restrictionTree);
    if (leaves.length === 0) return false;
    return leaves.some((n) => restrictedIds.has(n.categoryId) !== baselineRestrictedIds.has(n.categoryId));
};

/** Merge API ids into a Set, skipping ids the user already has as pending leaf edits. */
export const mergeDiscoveredIds = (
    prev: Set<number>,
    newIds: Set<number>,
    pendingLeafIds: Set<number>
): Set<number> => {
    const next = new Set(prev);
    newIds.forEach((id) => {
        if (!pendingLeafIds.has(id)) next.add(id);
    });
    return next;
};
